
import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Product } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import ProductForm from "./products/ProductForm";

interface AddProductSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (product: Omit<Product, "id" | "status" | "createdAt" | "createdBy">) => Promise<void>;
}

const AddProductSheet = ({ isOpen, onClose, onAdd }: AddProductSheetProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    // Check connection when sheet is opened
    const checkConnection = async () => {
      if (isOpen) {
        try {
          const { error } = await supabase
            .from('products')
            .select('count', { count: 'exact', head: true });
          
          setConnectionError(!!error);
          
          if (error) {
            console.error('Erreur de connexion Supabase:', error);
          }
        } catch (err) {
          console.error('Échec de la vérification de la connexion:', err);
          setConnectionError(true);
        }
      }
    };
    
    checkConnection();
  }, [isOpen]);

  const handleSave = async (productData: Omit<Product, "id" | "status" | "createdAt" | "createdBy">) => {
    setIsSubmitting(true);
    try {
      await onAdd(productData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="overflow-y-auto max-h-screen">
        <SheetHeader>
          <SheetTitle>Ajouter un nouveau produit</SheetTitle>
        </SheetHeader>

        {connectionError && (
          <Alert className="my-4 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">
              Problème de connexion à la base de données. Certaines fonctionnalités peuvent ne pas fonctionner correctement.
            </AlertDescription>
          </Alert>
        )}

        <ProductForm 
          onSave={handleSave} 
          isSubmitting={isSubmitting || connectionError} 
          onClose={onClose}
        />
      </SheetContent>
    </Sheet>
  );
};

export default AddProductSheet;
